import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, Sparkles, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialBanner() {
  const { token } = useAuth();
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!token) return;

    axios.get(`${API_URL}/api/subscriptions/plan-info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => setPlan(response.data))
      .catch(() => {});
  }, [token]);

  if (!plan) return null;

  // BILL-4a — ruling 1: read-only, never locked out. Shown only when writes
  // are actually refused (enforcement on); in observe mode nothing is.
  if (plan.access?.read_only) {
    return (
      <div
        className="bg-red-600 text-white px-4 py-2.5 text-sm flex items-center justify-center gap-3 flex-wrap"
        data-testid="read-only-banner"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="font-semibold">Northline is currently in Read-Only Mode</span>
        <span className="opacity-90" data-testid="read-only-reason">{plan.access.reason}</span>
        <span className="opacity-90 hidden md:inline">Your data is still here to view and export.</span>
        <Link
          to={plan.has_subscription ? '/settings?tab=subscription' : '/pricing'}
          className="bg-white text-red-700 px-3 py-1 rounded-md font-semibold text-xs hover:bg-red-50 transition-colors"
          data-testid="reactivate-subscription-link"
        >
          Reactivate Subscription
        </Link>
      </div>
    );
  }

  if (['unpaid', 'incomplete'].includes(plan.status)) {
    return (
      <div
        className="bg-red-600 text-white px-4 py-2.5 text-sm flex items-center justify-center gap-3 flex-wrap"
        data-testid="subscription-blocked-banner"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Northline billing requires attention.
        </span>
        <span className="opacity-90 hidden sm:inline">
          Resolve payment to restore package access.
        </span>
        <Link
          to="/settings?tab=subscription"
          className="bg-white text-red-700 px-3 py-1 rounded-md font-semibold text-xs hover:bg-red-50 transition-colors"
          data-testid="subscription-blocked-manage-link"
        >
          Manage Billing
        </Link>
      </div>
    );
  }

  if (plan.status === 'past_due') {
    return (
      <div
        className="bg-amber-700 text-white px-4 py-2 text-sm flex items-center justify-center gap-3 flex-wrap"
        data-testid="subscription-past-due-banner"
      >
        <Clock className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Your Northline payment is past due.
        </span>
        <span className="opacity-90 hidden sm:inline">
          {plan.access?.grace_ends_at
            ? `Full access continues until ${new Date(plan.access.grace_ends_at).toLocaleDateString()}. Update billing to prevent read-only mode.`
            : 'Update billing to prevent an interruption.'}
        </span>
        <Link
          to="/settings?tab=subscription"
          className="bg-white hover:bg-slate-100 text-amber-800 px-3 py-0.5 rounded-md text-xs font-semibold transition-colors"
          data-testid="subscription-past-due-manage-link"
        >
          Manage Billing
        </Link>
      </div>
    );
  }

  if (plan.status === 'paused') {
    return (
      <div
        className="bg-amber-700 text-white px-4 py-2 text-sm flex items-center justify-center gap-3 flex-wrap"
        data-testid="subscription-paused-banner"
      >
        <Clock className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Your Northline subscription is paused.
        </span>
        <Link
          to="/settings?tab=subscription"
          className="bg-white hover:bg-slate-100 text-amber-800 px-3 py-0.5 rounded-md text-xs font-semibold transition-colors"
          data-testid="subscription-paused-manage-link"
        >
          Manage Billing
        </Link>
      </div>
    );
  }

  if (plan.trial_expired || plan.status === 'trial_expired') {
    return (
      <div
        className="bg-red-600 text-white px-4 py-2.5 text-sm flex items-center justify-center gap-3 flex-wrap"
        data-testid="trial-expired-banner"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="font-medium">
          Your Northline trial has ended.
        </span>
        <span className="opacity-90 hidden sm:inline">
          Select a commercial package to continue operating.
        </span>
        <Link
          to="/pricing"
          className="bg-white text-red-700 px-3 py-1 rounded-md font-semibold text-xs hover:bg-red-50 transition-colors"
          data-testid="trial-upgrade-button"
        >
          View Packages
        </Link>
      </div>
    );
  }

  if (plan.status === 'trialing' && plan.trial_ends_at) {
    const end = new Date(plan.trial_ends_at);
    const days = Math.max(
      0,
      Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24))
    );
    const isUrgent = days <= 3;

    return (
      <div
        className={`${isUrgent ? 'bg-amber-700' : 'bg-cyan-700'} text-white px-4 py-2 text-sm flex items-center justify-center gap-3 flex-wrap`}
        data-testid="trial-active-banner"
      >
        {isUrgent
          ? <Clock className="w-4 h-4 shrink-0" />
          : <Sparkles className="w-4 h-4 shrink-0" />}

        <span className="font-medium">
          {days === 0
            ? 'Your Northline trial ends today'
            : `${days} day${days === 1 ? '' : 's'} left in your Northline trial`}
        </span>

        <Link
          to="/pricing"
          className={`bg-white hover:bg-slate-100 ${isUrgent ? 'text-amber-800' : 'text-cyan-800'} px-3 py-0.5 rounded-md text-xs font-semibold transition-colors`}
          data-testid="trial-upgrade-link"
        >
          View Packages
        </Link>
      </div>
    );
  }

  return null;
}
